import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';
import { useCurrentUser } from '@bonusly/who-am-i';

import { Bonus } from '../../../../../../types/bonus';
import { QueryKey } from '../../../hooks/types';
import { RecognizeResponse } from '../../../types/api';
import { isCelebration } from '../../../utils';
import { Reaction } from '../lib/reaction';
import { ReactionType } from '../types';
import { buildReaction } from '../utils';
import { useAddReactionToBonus } from './use-add-reaction-to-bonus';
import { useRemoveReactionFromBonus } from './use-remove-reaction-from-bonus';
import { useReplaceBonus } from './use-replace-bonus';

export const useCreateReaction = (bonus: Bonus) => {
	const user = useCurrentUser();
	const addReaction = useAddReactionToBonus();
	const removeReaction = useRemoveReactionFromBonus();
	const replaceBonus = useReplaceBonus();
	const http = useHttpClient('v2');
	const queryClient = useQueryClient();

	return useMutation(
		(type: ReactionType) => {
			const reaction = Reaction.fromType(type);

			return http.post<RecognizeResponse<Bonus>>('reactions', {
				bonus_id: bonus.id,
				emoji: reaction.shortcode,
			});
		},
		{
			onMutate: (type) => {
				const record = buildReaction(type, user);

				addReaction(bonus.id, record);

				return record._id;
			},
			onError: (_err, _type, tempId) => {
				if (tempId) {
					removeReaction(bonus.id, tempId);
				}
			},
			onSuccess: (data) => {
				const bonus = data.data.result;

				if (isCelebration(bonus)) {
					queryClient.invalidateQueries([QueryKey.CelebrationsWidget]);
				}

				replaceBonus(bonus);
			},
		}
	);
};
